import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll-restore:';
const WRITE_THROTTLE_MS = 250;
const RESTORE_MAX_MS = 2000;

const memoryStore = new Map();

const buildStorageKey = (_routerKey, pathname, search) => {
  const safeKey = `${pathname}${search || ''}`;
  return `${STORAGE_PREFIX}${safeKey}`;
};

const readY = (storageKey) => {
  let raw = null;
  try {
    raw = sessionStorage.getItem(storageKey);
  } catch {
    raw = memoryStore.has(storageKey) ? memoryStore.get(storageKey) : null;
  }
  if (raw == null) { return null; }
  const y = parseInt(raw, 10);
  return Number.isFinite(y) ? y : null;
};

const writeY = (storageKey, y) => {
  const value = String(y);
  memoryStore.set(storageKey, value);
  try {
    sessionStorage.setItem(storageKey, value);
  } catch {
    /* fall back to in-memory */
  }
};

export const useScrollRestoration = (ready = true) => {
  const { key, pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const restoredKeyRef = useRef(null);
  const restoreRafRef = useRef(null);
  const storageKey = buildStorageKey(key, pathname, search);

  useEffect(() => {
    if (navigationType !== 'POP' && restoredKeyRef.current !== storageKey) {
      window.scrollTo(0, 0);
      restoredKeyRef.current = storageKey;
    }
  }, [storageKey, navigationType]);

  useEffect(() => {
    if (navigationType !== 'POP') { return undefined; }
    if (!ready || restoredKeyRef.current === storageKey) { return undefined; }

    if (restoreRafRef.current !== null) {
      cancelAnimationFrame(restoreRafRef.current);
      restoreRafRef.current = null;
    }

    const y = readY(storageKey);
    if (y != null) {
      // Retry until layout is tall enough: lazy-loaded sections (e.g. Popular
      // Brands) mount after initial render, so target Y may exceed scrollHeight.
      const start = performance.now();
      const tick = () => {
        restoreRafRef.current = null;
        window.scrollTo(0, y);
        const reached = Math.abs(window.scrollY - y) < 2;
        const elapsed = performance.now() - start;
        if (!reached && elapsed < RESTORE_MAX_MS) {
          restoreRafRef.current = requestAnimationFrame(tick);
        }
      };
      restoreRafRef.current = requestAnimationFrame(tick);
    }

    restoredKeyRef.current = storageKey;

    return () => {
      if (restoreRafRef.current !== null) {
        cancelAnimationFrame(restoreRafRef.current);
        restoreRafRef.current = null;
      }
    };
  }, [ready, storageKey, navigationType]);

  useEffect(() => {
    let timeoutId = null;
    const save = () => {
      timeoutId = null;
      writeY(storageKey, window.scrollY);
    };
    const onScroll = () => {
      if (timeoutId !== null) { return; }
      timeoutId = setTimeout(save, WRITE_THROTTLE_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timeoutId !== null) { clearTimeout(timeoutId); }
      save();
    };
  }, [storageKey]);
};
